import { decodeApiText } from '../../../utils/errorUtils';
// Common Types - Shared across multiple API modules

// API Error types for type-safe error handling
export interface ApiErrorResponse {
  detail?: string;
  non_field_errors?: string[];
  message?: string;
  errors?: Record<string, string[]>;
  details?: {
    fields?: Record<string, string[]>;
  };
  [key: string]: unknown;
}

export interface ApiError extends Error {
  response?: {
    status: number;
    data?: ApiErrorResponse;
  };
  code?: string;
}

// Type guard for API errors
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as ApiError).response === 'object'
  );
}

// Helper to extract error message from API error
export function getApiErrorMessage(error: unknown, defaultMessage = 'An error occurred'): string {
  // The API HTML-escapes message text; this app renders it as text, so
  // give the caller readable quotes back (see utils/errorUtils).
  return decodeApiText(extractApiErrorMessage(error, defaultMessage));
}

function extractApiErrorMessage(error: unknown, defaultMessage: string): string {
  if (isApiError(error)) {
    const data = error.response?.data;
    if (data?.detail) return data.detail;
    if (data?.non_field_errors?.length) return data.non_field_errors[0];
    // Extract field-level validation errors from standardized response
    const details = data?.details;
    if (details && typeof details === 'object') {
      const fields = details['fields'] as Record<string, string[]> | undefined;
      if (fields && typeof fields === 'object') {
        const firstField = Object.keys(fields)[0];
        if (firstField) {
          const fieldErrors = fields[firstField];
          if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            return fieldErrors[0];
          }
        }
      }
    }
    if (data?.message) return data.message;
    // Plain DRF field validation errors: { "field_name": ["message"] }
    // Used by ModelSerializer.validate() when raising ValidationError.
    if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      for (const key of Object.keys(dataObj)) {
        const val = dataObj[key];
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
          return val[0];
        }
        if (typeof val === 'string' && key !== 'code') {
          return val;
        }
      }
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return defaultMessage;
}

// Base query params for paginated list endpoints
export interface QueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  ordering?: string;
  is_active?: boolean;
  [key: string]: string | number | boolean | undefined;
}

// Approval action response
export interface ActionResponse {
  status: string;
  message: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_active: boolean;
  /** Django auth last_login (null if never logged in) */
  last_login?: string | null;
  date_joined?: string;
  last_invitation_sent?: string | null;
  is_staff?: boolean;
  is_superuser?: boolean;
  is_company_admin?: boolean;
  /**
   * CR-7259: true when this admin's authority covers the whole tenant
   * (Helix users, or a customer admin holding every company in their
   * group; a customer with no group is its own tenant). Group-level
   * configuration such as Admin Setup > Performance is gated on it.
   * Not the same as `is_company_admin`, which a single-site admin has.
   */
  is_group_admin?: boolean;
  can_manage_users?: boolean;
  has_joined?: boolean;
  invitation_sent?: boolean;
  // Extended properties for UI
  username?: string;
  role?: string;
  platform?: 'CUSTOMER' | 'SUPPLIER' | 'HELIX';
  companyName?: string;
  company?: number;
  company_type?: 'customer' | 'supplier' | 'helix' | 'logistics';
  profile_id?: number;
  /** ERP buyer codes allocated to this user — when non-empty, purchasing
   *  list views default to "my suppliers" with a toggle to all. */
  buyer_codes?: string[];
  // Legal acceptance tracking
  terms_accepted_version?: number;
  privacy_accepted_version?: number;
  terms_current_version?: number;
  privacy_current_version?: number;
  // Supplier-only — null for non-supplier users. Used to gate sidebar until approved.
  supplier_is_approved?: boolean | null;
  supplier_onboarding_status?: 'draft' | 'pending_initial' | 'invited' | 'not_approved' | 'due_diligence' | 'approved' | null;
  // Customer ERP handler — drives tenant-specific UI (e.g. parts master columns
  // for 'pna', schedule columns for 'dennis_eagle'). Null for non-customer users
  // and for customers without an import profile.
  import_handler?: 'pna' | 'dennis_eagle' | 'default' | null;
}

/**
 * CR-6247: business-profile detail for a company, mirroring the equivalent
 * fields on a supplier profile. Nested under {@link Company} so the
 * Organisation edit pop-up saves everything in one PATCH.
 */
export interface CompanyProfileDetail {
  year_established?: number | null;
  employee_count?: number | null;
  /** Decimal serialised as a string by DRF. Formatted in `effective_currency`. */
  annual_revenue?: string | null;
}

export interface Company {
  id: number;
  name: string;
  company_type: 'customer' | 'supplier' | 'helix';
  group?: number;
  group_name?: string;
  primary_email: string;
  primary_phone?: string;
  website?: string;
  description?: string;
  // Address fields
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  latitude?: string | null;
  longitude?: string | null;
  // Additional fields
  registration_number?: string;
  tax_id?: string;
  /** CR-7363: the supplier's contact person as held in the ERP */
  contact_name?: string;
  fax?: string;
  /** CR-7363: other names the ERP holds for the company (foreign name, alias) */
  alternative_name?: string;
  short_name?: string;
  /** CR-7431: VAT / sales tax rate as a percentage string ("20.000").
   *  Null/absent when unset — not the same as a zero-rated 0. Printed on
   *  this company's purchase orders. */
  tax_rate?: string | null;
  supplier_code?: string;
  /** CR-6438: customer (site) ids this supplier serves — set on supplier rows from the for-filter list. */
  customers?: number[] | null;
  // CR-6247: currency + business profile shown on the Company Profile page.
  /** This row's own currency. Dormant while `uses_group_regional_defaults`. */
  base_currency?: string;
  /** Currency actually in force — group default when the site inherits. */
  effective_currency?: string;
  /** Every regional value in force, resolved through the inherit/override choice. */
  effective_regional_settings?: {
    currency: string;
    timezone: string;
    language: string;
    date_format: string;
  };
  /** True while the site takes currency/timezone/language/date format from its group. */
  uses_group_regional_defaults?: boolean;
  profile?: CompanyProfileDetail | null;
  site_count: number;
  user_count: number;
  is_active: boolean;
  created_at: string;
  // CR-3396: Portal mode for dual-role companies
  portal_mode?: 'customer' | 'supplier' | 'both';
  /** CR-7255 review: false when no portal mode is saved — `portal_mode` then
   *  reads 'both' while the platform keeps users on the company's own side. */
  portal_mode_is_set?: boolean;
  /** CR-7255: platform-staff marker — this customer company also trades as a
   *  supplier. Written only through coreAPI.setDualIdentity. */
  is_also_supplier?: boolean;
  /** CR-7255: customer-typed AND marked. The one read for "does this company
   *  have both portal sides". */
  is_dual_sided?: boolean;
  // Customer Portal Mode: Enable suppliers to manage their customers
  customer_portal_enabled?: boolean;
}

export interface Site {
  id: number;
  company: number;
  company_name: string;
  name: string;
  code: string;
  site_type: string;
  city: string;
  country: string;
  warehouse_count: number;
  is_active: boolean;
  group_id?: number | null;
  group_name?: string | null;
}

export interface Warehouse {
  id: number;
  site: number;
  site_name: string;
  name: string;
  code: string;
  warehouse_type: string;
  address: string;
  city: string;
  country: string;
  location_count: number;
  is_active: boolean;
}

export interface Location {
  id: number;
  warehouse: number;
  warehouse_name: string;
  site_name: string;
  company_name: string;
  name: string;
  code: string;
  location_type: string;
  aisle: string;
  shelf: string;
  bin_number: string;
  capacity?: number;
  max_weight?: string;
  description: string;
  full_path: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CompanyGroup {
  id: number;
  name: string;
  description: string;
  headquarters_country: string;
  website: string;
  parent_group?: number;
  parent_group_name?: string;
  /** CR-7411: top of this group's tree (the group itself when it has no
   *  parent). Only the /company-groups/accessible/ endpoint returns it. */
  root_group?: number;
  root_group_name?: string;
  level: number;
  company_count: number;
  child_group_count: number;
  /** Side of the hierarchy the group was created on. Places groups that have
   *  no member companies; groups with members are resolved from those members. */
  company_type?: 'customer' | 'supplier' | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** CR-4579: Response from /company-groups/:id/members/ endpoint */
export interface CompanyGroupMembersResponse {
  group: CompanyGroup;
  companies: CompanyListItem[];
  stats: {
    total_companies: number;
    total_orders: number;
    total_order_value: string;
    total_grns: number;
  };
}

/** Minimal company fields returned by CompanyListSerializer */
export interface CompanyListItem {
  id: number;
  name: string;
  company_type: string;
  group: number | null;
  group_name: string | null;
  primary_email: string;
  city: string;
  country: string;
  site_count: number;
  user_count: number;
  is_active: boolean;
  created_at: string;
}

// Hierarchy tree node types - matches backend API response
export interface HierarchyNode {
  id: string; // Prefixed ID like "group_1", "company_2"
  entity_id: number; // Actual database ID
  type: 'group' | 'division' | 'company' | 'warehouse' | 'location';
  name: string;
  is_active: boolean;
  children: HierarchyNode[];
  // Type-specific fields
  code?: string;
  site_type?: string;
  warehouse_type?: string;
  location_type?: string;
  company_type?: string;
  level?: number;
  description?: string;
  metadata?: {
    city?: string;
    country?: string;
    headquarters_country?: string;
    website?: string;
    primary_email?: string;
    manager_name?: string;
  };
}

export interface HierarchyTreeResponse {
  tree: HierarchyNode[];
  stats: {
    total_groups: number;
    total_divisions: number;
    total_companies: number;
    total_warehouses: number;
    total_locations: number;
  };
  labels?: {
    group: string;
    division: string;
    site: string;
  };
}

export interface Role {
  id: number;
  name: string;
  description: string;
  company_type: 'helix' | 'customer' | 'supplier';
  department: 'admin' | 'purchasing' | 'quality' | 'finance' | 'operations' | 'logistics' | 'engineering' | 'custom';
  department_display: string;
  is_admin_role: boolean;
  // CR-5275: when true, users with this role are forced through TOTP MFA on login.
  mfa_required: boolean;
  // General permissions
  can_manage_users: boolean;
  can_manage_sites: boolean;
  can_view_all_sites: boolean;
  can_manage_suppliers: boolean;
  can_manage_customers: boolean;
  // Purchasing permissions
  can_manage_orders: boolean;
  can_approve_orders: boolean;
  can_view_pricing: boolean;
  // Quality permissions
  can_manage_quality: boolean;
  can_create_ncr: boolean;
  can_approve_ncr: boolean;
  // Finance permissions
  can_view_financials: boolean;
  can_manage_invoices: boolean;
  // Reporting
  can_view_reports: boolean;
  can_export_data: boolean;
  permissions_summary: string;
  user_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** CR-7255: the role a user holds on one portal side. */
export interface UserSideRole {
  side: 'customer' | 'supplier';
  side_display: string;
  role: number;
  role_name: string;
  is_admin_role: boolean;
}

export interface UserProfile {
  id: number;
  user: User;
  company: number;
  company_name: string;
  company_type: 'customer' | 'supplier' | 'helix' | 'logistics';
  role: number;
  role_name: string;
  role_details?: Role;
  /** CR-7255: per-side roles. Empty unless the company trades on both sides;
   *  the customer-side row always mirrors `role`. */
  side_roles?: UserSideRole[];
  /** CR-7255: true when the user's company is a dual (customer + supplier) company. */
  is_dual_company?: boolean;
  job_title?: string;
  department?: string;
  phone?: string;
  sites: number[];
  site_names: string[];
  /** ERP buyer codes this user looks after — when non-empty, purchasing
   *  list views default to "my suppliers" (rows whose PO line carries one
   *  of these codes) with a toggle to all suppliers. */
  buyer_codes?: string[];
  is_approved: boolean;
  /** CR-6085: per-row admin-tier verdict. False means the requesting admin
   *  is not entitled to perform mutating User Management actions on this
   *  row (lower-tier or sibling-tier admin). View-only is still allowed. */
  can_manage_user?: boolean;
  /** Companies this user is the designated primary contact for. Read-only —
   *  writes go through coreAPI.setPrimaryContacts. */
  primary_contact_for?: PrimaryContactFor[];
  /** Platform-staff flag. Serialized only for superusers — `null` for every
   *  other viewer, who has no business seeing or changing it. Writes go
   *  through coreAPI.setPlatformStaff. */
  is_platform_staff?: boolean | null;
  created_at: string;
  updated_at?: string;
}

/** CR-7255: a relationship in which the marked company is the supplier. */
export interface DualSupplierRelationship {
  id: number;
  customer: number;
  customer_name: string;
  supplier_code: string;
  is_active: boolean;
  relationship_type: string;
  supplier_codes: { supplier_code: string; is_primary: boolean; is_active: boolean }[];
}

/** CR-7255 review: `GET /core/companies/{id}/users-access/` — whether the
 *  caller may read the company's Users tab, asked before reading it. */
export interface CompanyUsersAccess {
  can_view: boolean;
  /** Why not: `manages_own_users` for a customer site that also supplies the
   *  caller (it keeps its own admins), else `forbidden`; null when allowed. */
  code: 'manages_own_users' | 'forbidden' | null;
}

/** CR-7255: what GET/POST /companies/{id}/dual-identity/ returns. */
export interface DualIdentityState {
  company: number;
  company_type: 'customer' | 'supplier' | 'helix';
  is_also_supplier: boolean;
  is_dual_sided: boolean;
  can_be_dual: boolean;
  supplier_relationships: DualSupplierRelationship[];
  /** Active supplier-typed companies with this company's name: the duplicate an
   *  ERP feed creates when the marker and code were missing. */
  name_twins: { id: number; name: string; supplier_code: string; relationship_count: number }[];
  message?: string;
}

/** One primary-contact designation as embedded on a UserProfile. */
export interface PrimaryContactFor {
  id: number;
  company: number;
  company_name: string;
  contact_type: 'supplier' | 'customer';
}

/** A primary-contact designation as returned by /core/primary-contacts/. */
export interface PrimaryContactAssignment {
  id: number;
  user_profile: number;
  user_id: number;
  user_name: string;
  user_email: string;
  company: number;
  company_name: string;
  contact_type: 'supplier' | 'customer';
  created_at: string;
  updated_at: string;
}

/** A pickable company, annotated with whoever currently holds the designation. */
export interface PrimaryContactCompanyOption {
  id: number;
  name: string;
  company_type: 'supplier' | 'customer';
  supplier_code: string;
  current_contact: {
    user_profile: number;
    name: string;
    email: string;
  } | null;
}

/** Response of the set-primary-contacts call. `replaced` lists companies whose
 *  previous holder was displaced, so the UI can report the steal. */
export interface PrimaryContactSetResponse {
  assignments: PrimaryContactAssignment[];
  replaced: {
    company: number;
    company_name: string;
    previous_contact: string;
  }[];
}

export interface UserManagementLog {
  id: number;
  performed_by_email: string;
  performed_by_name: string;
  target_user_email: string;
  target_user_name: string;
  company_name: string;
  action: string;
  action_display: string;
  description: string;
  old_value: Record<string, unknown>;
  new_value: Record<string, unknown>;
  ip_address?: string;
  timestamp: string;
}

export interface UserSitePermission {
  id: number | null;
  user_profile: number;
  site: number;
  site_name: string;
  site_code: string;
  user_email: string;
  is_linked: boolean;
  is_active: boolean;
  can_purchasing: boolean;
  can_quality: boolean;
  can_finance: boolean;
  /** CR-5313 compliance module access. Serialised by the API alongside the others. */
  can_compliance: boolean;
  is_admin: boolean;
  can_approve: boolean;
  is_read_only: boolean;
  notify_procurement: boolean;
  notify_quality: boolean;
  notify_compliance: boolean;
  /** CR-6457: receive the Weekly Arrears Report email for this site. */
  notify_weekly_arrears: boolean;
  created_at?: string;
  updated_at?: string;
  // CR-6076 AC3: division (immediate group) the site belongs to, plus the
  // division's parent (top-level group), so the Related Companies tab can
  // render the full Group -> Division -> Site tree.
  group_id?: number | null;
  group_name?: string | null;
  parent_group_id?: number | null;
  parent_group_name?: string | null;
  // CR-5311: synthetic group-membership row (read-only) prepended by the API.
  is_group_entry?: boolean;
  role_name?: string | null;
}

export interface DashboardStats {
  total_companies: number;
  total_customers: number;
  total_suppliers: number;
  total_sites: number;
  total_users: number;
  pending_approvals: number;
  active_relationships: number;
  recent_registrations: number;
}

export interface CompanyRegistrationData {
  name: string;
  company_type: 'customer' | 'supplier';
  description?: string;
  website?: string;
  primary_email: string;
  primary_phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  user_password: string;
  user_password_confirm: string;
}

// Impersonation Types
export interface ImpersonatableUser {
  user_id: number;
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
  job_title?: string;
  role?: string;
  is_active: boolean;
}

export interface ImpersonatableCompany {
  company_id: number;
  company_name: string;
  company_type: 'customer' | 'supplier';
  users: ImpersonatableUser[];
}

export interface ImpersonateResponse {
  message: string;
  impersonating: boolean;
  original_user: User;
  original_token: string;
  target_user: User;
  target_token: string;
  token: string;
}

export interface StopImpersonateResponse {
  message: string;
  impersonating: boolean;
  user: User;
  token: string;
}

// View Context types (for database-backed view switching)
export interface ViewContext {
  current_view_level: 'GROUP' | 'COMPANY' | 'SITE';
  current_group: number | null;
  current_group_name: string | null;
  current_company: number | null;
  current_company_name: string | null;
  current_site: number | null;
  current_site_name: string | null;
  portal_mode: 'customer' | 'supplier' | 'customer_manager';
  has_site_restrictions: boolean;
  platform_role: 'owner' | 'guest';
  enabled_modules: string[] | null;  // null = all modules enabled
}

/** CR-4802: the backend's filter-visibility signals (snake_case as served). */
export interface FilterVisibilitySignals {
  division: boolean;
  warehouse: boolean;
  supplier_code: boolean;
  supplier_type: boolean;
  historical_live: boolean;
  company_country: boolean;
  commodity: boolean;
}

/** CR-4802: `/core/settings/filter-visibility/` response. */
export interface FilterVisibilityPayload {
  /** What the panels gate on: declared value where set, else detected. */
  flags: FilterVisibilitySignals;
  /** Detected defaults from the tenant's own data. */
  detected: FilterVisibilitySignals;
  /** Signals the tenant has declared explicitly. */
  overrides: Partial<Record<keyof FilterVisibilitySignals, boolean>>;
  /** True when the viewer may declare the shape. */
  editable: boolean;
}

/** CR-4802: PUT body — `null` clears a declaration back to detection. */
export type FilterVisibilityOverrides = Partial<Record<
  Exclude<keyof FilterVisibilitySignals, 'commodity'>, boolean | null
>>;

export interface ViewContextUpdate {
  current_view_level?: 'GROUP' | 'COMPANY' | 'SITE';
  current_group?: number | null;
  current_company?: number | null;
  current_site?: number | null;
  portal_mode?: 'customer' | 'supplier' | 'customer_manager';
}

// Dashboard Tab Visibility (CR-4441)
export interface DashboardTabsVisibility {
  'default': boolean;
  'group-kpi': boolean;
  'group-detail-kpi': boolean;
  'site-kpi': boolean;
  'cost': boolean;
  'emissions': boolean;
  'compliance': boolean;
  'future-forecast': boolean;
  'date-adherence': boolean;
  'expediting': boolean;
  'performance-ratings': boolean;
  [key: string]: boolean;
}

// Supplier Card Field Visibility (approved supplier directory cards)
export interface SupplierCardFieldsVisibility {
  logo: boolean;
  risk_badge: boolean;
  otd: boolean;
  rating: boolean;
  questionnaire_status: boolean;
  /** Show the OTIF-band quick-filter chip row on the /suppliers list. */
  otif_quick_filter: boolean;
  otif: boolean;
  [key: string]: boolean;
}

// Supplier Profile page layout (per-tenant). Controls which stat cards,
// tabs and overview widgets appear on /suppliers/:id.
export interface SupplierProfileLayout {
  stats: { otd: boolean; total_orders: boolean; [key: string]: boolean };
  tabs: {
    performance: boolean; orders: boolean; contracts: boolean;
    quality: boolean; questionnaires: boolean; credit_scores: boolean;
    users: boolean; activity_log: boolean; group_structure: boolean;
    [key: string]: boolean;
  };
  overview: { performance_summary: boolean; capabilities: boolean; [key: string]: boolean };
}

// Help & Support page layout (per-tenant, superuser-editable). Used to hide
// FAQ categories and sidebar widgets that are irrelevant for a given tenant
// (e.g. MAB Compliance hides Purchasing because they don't run POs).
export interface HelpLayout {
  faq: {
    getting_started: boolean;
    onboarding: boolean;
    questionnaires: boolean;
    purchasing: boolean;
    supplier_management: boolean;
    documents_communication: boolean;
    [key: string]: boolean;
  };
  sidebar: {
    tour: boolean;
    contact_support: boolean;
    system_status: boolean;
    [key: string]: boolean;
  };
}

// Data Import Tile Visibility (individual upload tiles on /data-import)
export interface DataImportTilesVisibility {
  commodity: boolean;
  faultcodes: boolean;
  commonised_ca: boolean;
  departments: boolean;
  warehouses: boolean;
  suppliers: boolean;
  supplier_additional: boolean;
  grns: boolean;
  pos: boolean;
  ncrs: boolean;
  forecasts: boolean;
  users: boolean;
  [key: string]: boolean;
}

// Purchasing Tab Visibility
export interface PurchasingTabsVisibility {
  rfq: boolean;
  pricing: boolean;
  po_headers: boolean;
  po_lines: boolean;
  forecast: boolean;
  asn: boolean;
  deliveries: boolean;
  call_off: boolean;
  [key: string]: boolean;
}

// Quality Tab Visibility
export interface QualityTabsVisibility {
  ncrs: boolean;
  concessions: boolean;
  approval_register: boolean;
  [key: string]: boolean;
}

// Open Orders Visibility (CR-4422) - ON = hidden in Open Orders view
export interface OpenOrdersVisibility {
  hide_exceptions: boolean;
  hide_decline_button: boolean;
  hide_propose_qty_ordered: boolean;
  hide_actions: boolean;
  hide_trend_line: boolean;
  hide_otif_override_button: boolean;
  hide_forecasting_from_supplier: boolean;
  hide_communication_box: boolean;
  [key: string]: boolean;
}

// CR-5892: feature flags a client admin can manage on their own tenant via
// Admin Setup (settings/company-feature-flags/), without the Helix-only
// platform settings endpoint.
export interface ClientFeatureFlags {
  allow_supplier_decline: boolean;
}

/**
 * Per-customer delivery grain — whether a customer's deliveries are judged
 * on POLineSchedule rows ('schedules') or PO-line headers ('po_lines').
 * 'platform' (stored only) means "inherit the po_line_schedules flag".
 */
export type DeliveryGrain = 'schedules' | 'po_lines';
export type DeliveryGrainSetting = 'platform' | DeliveryGrain;
/** A multi-site view's grain: one of the two, or 'mixed' (each site judged on its own). */
export type GroupDeliveryGrain = DeliveryGrain | 'mixed';

/** One customer row from settings/delivery-grain/ (Helix-only). */
export interface CustomerDeliveryGrain {
  id: number;
  name: string;
  is_active: boolean;
  delivery_grain: DeliveryGrainSetting;
  effective_grain: DeliveryGrain;
}

export interface DeliveryGrainSettingsResponse {
  platform_po_line_schedules: boolean;
  platform_grain: DeliveryGrain;
  choices: { value: DeliveryGrainSetting; label: string }[];
  customers: CustomerDeliveryGrain[];
}

/** How a customer's forecast lines are attributed to suppliers. */
export type ForecastScope = 'supplier' | 'part';

/** One customer row from settings/forecast-scope/ (Helix-only). */
export interface CustomerForecastScope {
  id: number;
  name: string;
  is_active: boolean;
  forecast_scope: ForecastScope;
  /** Schedule-based customers derive forecast from schedule lines and ignore the scope. */
  schedule_based: boolean;
  effective_mode: 'schedule' | ForecastScope;
  has_profile: boolean;
}

export interface ForecastScopeSettingsResponse {
  choices: { value: ForecastScope; label: string }[];
  customers: CustomerForecastScope[];
}

/** Per-scope grain fields the display-settings endpoint adds alongside the platform flags. */
export interface DisplaySettingsDeliveryGrain {
  effective_po_line_schedules?: boolean;
  delivery_grain?: DeliveryGrain;
  delivery_grain_customer_id?: number | null;
  /** Grain of a multi-site view — 'mixed' when it holds sites of both grains; null for one site. */
  group_delivery_grain?: GroupDeliveryGrain | null;
  /** Mixed-grain view only: its PO-line-grain sites (Purchasing names them on the Schedules tab). */
  group_po_line_sites?: Array<{ id: number; name: string }>;
}

// Menu Visibility
export interface MenuVisibility {
  // Customer Menu Items
  dashboard: boolean;
  supplierPerformance: boolean;
  calendar: boolean;
  purchasing: boolean;
  rfq: boolean;
  quality: boolean;
  concessions: boolean;
  suppliers: boolean;
  /**
   * CR-8672: customer-side Supplier Onboarding (/onboarding). Not to be
   * confused with `supplierOnboarding`, the supplier's own My Onboarding.
   */
  onboarding: boolean;
  documents: boolean;
  supplierIntelligence: boolean;
  supplierOnboarding: boolean;
  userInvitations: boolean;
  userActivityHistory: boolean;
  questionnaire: boolean;
  newsRelease: boolean;
  adminSetup: boolean;
  partsMaster: boolean;
  /** Isolated Part Schedule module — per-install opt-in via Admin Setup.
   *  Backend additionally gates by CustomerImportProfile.part_schedule_module_enabled. */
  partSchedule: boolean;
  /** Analytics / Reports sidebar item (customer + supplier share the same key). */
  analytics: boolean;
  dataImport: boolean;
  // Supplier Menu Items
  purchaseOrders: boolean;
  companyProfile: boolean;
  customers: boolean;  // Customers list for supplier portal
  callOff: boolean;    // Call Off tab in Purchasing
  // Supplier-side navigation toggles — separate from the customer-side
  // keys so a tenant can keep e.g. News Release on for customer users
  // but switched off for supplier users, or hide Dashboard on the
  // supplier sidebar without affecting the customer one.
  supplierDashboard: boolean;
  supplierQuestionnaires: boolean;
  supplierCalendar: boolean;
  supplierCustomers: boolean;
  supplierDocuments: boolean;
  supplierNewsRelease: boolean;
  /** SI-16: supplier-side Supplier Performance page (own scores, one row per customer). */
  supplierSelfPerformance: boolean;
  /** Supplier-side RFQs sidebar entry. Off by default — switched on per install. */
  supplierRfq: boolean;
  // Allow additional boolean properties for backwards compatibility
  [key: string]: boolean;
}

/**
 * CR-8227: the shape of a customer's organisation, as recorded in Platform
 * Config. '' means not recorded yet. A record only; it changes no behaviour.
 */
export type OrganisationStructure = '' | 'single_site' | 'group_sites' | 'group_divisions_sites';

// Platform Settings
export interface PlatformSettings {
  id: number;
  /**
   * CR-6256: platform-wide regional defaults — the floor of the
   * Platform -> Group -> Site cascade, applied only where neither a site
   * override nor any ancestor group sets a value.
   */
  default_currency: string;
  default_timezone: string;
  default_date_format: string;
  // Branding & Theme
  company_name: string;
  default_theme: number;
  available_themes: string[];
  // Colors
  background_color: string;
  login_color: string;
  font_color: string;
  button_color: string;
  // CR-6243: Branding images. Serialised as URLs on read; written by
  // multipart upload (platformSettingsAPI.uploadBrandingImage). Null until an
  // admin uploads one.
  company_logo?: string | null;
  company_dark_logo?: string | null;
  header_logo?: string | null;
  login_image?: string | null;
  // Contact Information
  website: string;
  support_email: string;
  sales_email: string;
  no_reply_email: string;
  // Email Settings
  email_subject: string;
  email_signature: string;
  // Platform Configuration
  main_menu_type: string;
  free_mode: boolean;
  main_company_can_login_as: boolean;
  menu_visibility: MenuVisibility;
  // Feature Toggles
  automation_related: boolean;
  use_disposition_in_ncr: boolean;
  warehouse_module: boolean;
  turn_email_mab: boolean;
  switch_part_repository: boolean;
  quality_measure_ppk: boolean;
  /** Master switch for the 8D/A3 scoring feature (RC/CA/8D columns + grading input). */
  enable_8d_scoring: boolean;
  // NCR Template
  ncr_template: 1 | 2;
  // Barratt Feature Flags
  feature_flags?: {
    delivery_appeals?: boolean;
    spend_leakage?: boolean;
    bulk_upload?: boolean;
    commodity_codes?: boolean;
    supplier_stock_levels?: boolean;
    allow_supplier_decline?: boolean;
    po_line_schedules?: boolean;
    show_forecast_total?: boolean;
    forecast_weeks_only?: boolean;
    forecast_hide_supplier_part_no?: boolean;
    forecast_description_from_master?: boolean;
    forecast_show_po_number?: boolean;  // Forecast → PO Number column (unset = delivery grain)
    asn_produmax_export?: boolean;
    call_off_customer_haulage_cost?: boolean;  // CR-7781: customer may change the haulage cost, with a reason
    concessions_enabled?: boolean;  // CR-6385: concession workflow master switch (default OFF)
    ncr_escape_root_cause_enabled?: boolean;  // CR-7353: NCR Escape root-cause chain (default OFF)
    ncr_share_8d_scores_with_supplier?: boolean;  // QUALITY-NCR-REVIEW E5: suppliers see their 8D grades (default OFF)
    asn_logistics_tracking?: boolean;  // CR-6160: paid ASN courier-tracking module (Helix-admin, platform-level)
    grn_repair?: boolean;  // CR-6274: GRN Repair module switch (absent = on)
    portal_mode?: 'standard' | 'spend_analytics' | 'compliance' | 'barratt';  // CR-5334: 'barratt' → 'spend_analytics'
  };
  // IP Restriction
  restrict_country: string;
  restrict_country_enabled: boolean;
  // Upload Limits (Feature 5)
  max_upload_size_mb: number;
  allowed_file_types: string[];
  // Document Versioning (Feature 8)
  auto_version_documents: boolean;
  version_format: 'numeric' | 'semantic';
  retain_old_versions: boolean;
  // Notification Defaults (Feature 3)
  default_notification_preferences: Record<string, boolean>;
  system_alert_emails: string;
  // Supplier Onboarding
  supplier_onboarding_enabled: boolean;
  // Dashboard Tab Visibility (CR-4441)
  dashboard_tabs: DashboardTabsVisibility;
  supplier_dashboard_tabs: DashboardTabsVisibility;
  purchasing_tabs: PurchasingTabsVisibility;
  quality_tabs: QualityTabsVisibility;
  supplier_card_fields: SupplierCardFieldsVisibility;
  data_import_tiles: DataImportTilesVisibility;
  supplier_default_landing_path: string;
  /** Week-start boundary for weekly OTIF/dashboard windows: 0=Monday, 6=Sunday. */
  week_start_day: number;
  // NCR Leadtime Settings
  containment_action_leadtime: number;
  full_corrective_action_leadtime: number;
  /** E4: what the customer calls its own NCR number. */
  ncr_customer_reference_label: string;
  // Approval Expiry Alert
  approval_expiry_alert_days: number;
  // CR-5534: Quality Approval Register expiry-warning threshold (days).
  accreditation_expiry_warning_days: number;
  // NCR Email Notification Settings
  ncr_customer_department: string;
  ncr_supplier_department: string;
  ncr_email_frequency: 'weekly' | 'weekdays';
  ncr_email_weekdays: number[];
  // Purchasing Email Notification Settings (CR-4421)
  purchasing_customer_department: string;
  purchasing_supplier_department: string;
  purchasing_email_frequency: 'weekly' | 'weekdays';
  purchasing_email_weekdays: number[];
  purchasing_arrears_report_supplier: boolean;
  purchasing_arrears_report_customer: boolean;
  /** CR-6457: Weekly Arrears Report send day, 0=Sunday .. 6=Saturday. Held per organisation. */
  purchasing_arrears_report_weekday: number;
  // Auto Approve Delivery Appeal (CR-4423; CR-5885 added configurable delay)
  auto_approve_delivery_appeal: boolean;
  auto_approve_delivery_appeal_days: number;
  // CR-7068: Auto-Raise Delivery Appeals. CR-8283: per customer, never
  // inherited from the platform value; a Helix user reads the platform row.
  auto_raise_delivery_appeals: boolean;
  /** CR-8283: the day the customer turned automatic appeals on (YYYY-MM-DD); null while off or for a Helix user. */
  auto_raise_delivery_appeals_enabled_on?: string | null;
  // Open Orders Visibility (CR-4422)
  open_orders_visibility: OpenOrdersVisibility;
  // Price Book Sharing (CR-6346). 'custom' is system-set (a supplier-level
  // override exists) and is read-only in the UI.
  price_book_sharing_flag: 'all_off' | 'all_on' | 'custom';
  shared_price_book_types: number[];
  /** CR-7231: days before a contract's end date its renewal reminder is due (1-365). */
  contract_expiry_alert_days: number;
  // Cost of Quality
  ncr_cost_of_quality: number;
  /** CR-8227: the organisation's recorded shape; '' until the Helix team records it. */
  organisation_structure: OrganisationStructure;
  // Hierarchy Labels
  hierarchy_level_1_label: string;
  hierarchy_level_2_label: string;
  hierarchy_level_3_label: string;
  // Legal Content
  terms_content: string;
  privacy_content: string;
  terms_version: number;
  privacy_version: number;
  terms_updated_at: string | null;
  privacy_updated_at: string | null;
  /** Wording shown when a supplier is asked to acknowledge a document; '' = standard. */
  document_acknowledgement_message: string;
  // Metadata
  updated_at: string;
}

export interface LegalContent {
  terms_content: string;
  privacy_content: string;
  terms_version: number;
  privacy_version: number;
  terms_updated_at: string | null;
  privacy_updated_at: string | null;
}

export interface LegalAcceptRequest {
  terms_version?: number;
  privacy_version?: number;
}

export interface LegalAcceptResponse {
  message: string;
  terms_accepted_version: number;
  privacy_accepted_version: number;
}

export interface PlatformSettingsUpdate {
  // CR-6256: platform-wide regional defaults (cascade floor)
  default_currency?: string;
  default_timezone?: string;
  default_date_format?: string;
  company_name?: string;
  default_theme?: number;
  available_themes?: string[];
  background_color?: string;
  login_color?: string;
  font_color?: string;
  button_color?: string;
  website?: string;
  support_email?: string;
  sales_email?: string;
  no_reply_email?: string;
  // CR-8681: Admin Setup > Branding > Main Company Details
  contact_email?: string;
  company_phone?: string;
  company_address?: string;
  email_subject?: string;
  email_signature?: string;
  main_menu_type?: string;
  free_mode?: boolean;
  main_company_can_login_as?: boolean;
  menu_visibility?: Partial<MenuVisibility>;
  automation_related?: boolean;
  use_disposition_in_ncr?: boolean;
  warehouse_module?: boolean;
  turn_email_mab?: boolean;
  switch_part_repository?: boolean;
  quality_measure_ppk?: boolean;
  enable_8d_scoring?: boolean;
  // Barratt Feature Flags
  feature_flags?: {
    delivery_appeals?: boolean;
    spend_leakage?: boolean;
    bulk_upload?: boolean;
    commodity_codes?: boolean;
    supplier_stock_levels?: boolean;
    allow_supplier_decline?: boolean;
    po_line_schedules?: boolean;
    show_forecast_total?: boolean;
    forecast_weeks_only?: boolean;
    forecast_hide_supplier_part_no?: boolean;
    forecast_description_from_master?: boolean;
    forecast_show_po_number?: boolean;  // Forecast → PO Number column (unset = delivery grain)
    asn_produmax_export?: boolean;
    call_off_customer_haulage_cost?: boolean;  // CR-7781: customer may change the haulage cost, with a reason
    concessions_enabled?: boolean;  // CR-6385: concession workflow master switch (default OFF)
    ncr_escape_root_cause_enabled?: boolean;  // CR-7353: NCR Escape root-cause chain (default OFF)
    ncr_share_8d_scores_with_supplier?: boolean;  // QUALITY-NCR-REVIEW E5: suppliers see their 8D grades (default OFF)
    // CR-5334: writes must not persist the legacy client-named 'barratt' value.
    // The read type (PlatformSettings) still tolerates it for back-compat with
    // rows not yet migrated; this write type only allows the neutral values.
    portal_mode?: 'standard' | 'spend_analytics' | 'compliance';
  };
  restrict_country?: string;
  restrict_country_enabled?: boolean;
  // Upload Limits
  max_upload_size_mb?: number;
  allowed_file_types?: string[];
  // Document Versioning
  auto_version_documents?: boolean;
  version_format?: 'numeric' | 'semantic';
  retain_old_versions?: boolean;
  // Notification Defaults
  default_notification_preferences?: Record<string, boolean>;
  system_alert_emails?: string;
  // Supplier Onboarding
  supplier_onboarding_enabled?: boolean;
  // Dashboard Tab Visibility (CR-4441)
  dashboard_tabs?: Partial<DashboardTabsVisibility>;
  supplier_dashboard_tabs?: Partial<DashboardTabsVisibility>;
  purchasing_tabs?: Partial<PurchasingTabsVisibility>;
  quality_tabs?: Partial<QualityTabsVisibility>;
  supplier_card_fields?: Partial<SupplierCardFieldsVisibility>;
  data_import_tiles?: Partial<DataImportTilesVisibility>;
  supplier_default_landing_path?: string;
  /** Week-start boundary for weekly OTIF/dashboard windows: 0=Monday, 6=Sunday. */
  week_start_day?: number;
  // NCR Leadtime Settings
  containment_action_leadtime?: number;
  full_corrective_action_leadtime?: number;
  ncr_customer_reference_label?: string;
  // Approval Expiry Alert
  approval_expiry_alert_days?: number;
  // CR-5534: Quality Approval Register expiry-warning threshold (days)
  accreditation_expiry_warning_days?: number;
  // NCR Email Notification Settings
  ncr_customer_department?: string;
  ncr_supplier_department?: string;
  ncr_email_frequency?: 'weekly' | 'weekdays';
  ncr_email_weekdays?: number[];
  // Purchasing Email Notification Settings (CR-4421)
  purchasing_customer_department?: string;
  purchasing_supplier_department?: string;
  purchasing_email_frequency?: 'weekly' | 'weekdays';
  purchasing_email_weekdays?: number[];
  purchasing_arrears_report_supplier?: boolean;
  purchasing_arrears_report_customer?: boolean;
  purchasing_arrears_report_weekday?: number;
  // Auto Approve Delivery Appeal (CR-4423; CR-5885 added configurable delay)
  auto_approve_delivery_appeal?: boolean;
  auto_approve_delivery_appeal_days?: number;
  // CR-7068: Auto-Raise Delivery Appeals
  auto_raise_delivery_appeals?: boolean;
  // Open Orders Visibility (CR-4422)
  open_orders_visibility?: Partial<OpenOrdersVisibility>;
  // Price Book Sharing (CR-6346). All three states are admin-selectable;
  // 'custom' grants access per supplier via the supplier picker.
  price_book_sharing_flag?: 'all_off' | 'all_on' | 'custom';
  shared_price_book_types?: number[];
  /** CR-7231: Contract Expiry Alert, days prior (1-365). */
  contract_expiry_alert_days?: number;
  // Cost of Quality
  ncr_cost_of_quality?: number;
  /** CR-8227: Helix only; a record of the organisation's shape. */
  organisation_structure?: OrganisationStructure;
  // Hierarchy Labels
  hierarchy_level_1_label?: string;
  hierarchy_level_2_label?: string;
  hierarchy_level_3_label?: string;
  // Legal Content
  terms_content?: string;
  privacy_content?: string;
  terms_version?: number;
  privacy_version?: number;
  terms_updated_at?: string | null;
  privacy_updated_at?: string | null;
  document_acknowledgement_message?: string;
}

// Country Detection Response (Feature 7)
export interface CountryDetectionResponse {
  ip: string;
  country: string;
  country_code: string;
  message?: string;
}

// Merge Companies Response (Feature 1)
export interface MergeCompaniesResponse {
  message: string;
  target_company: { id: number; name: string };
  source_company_deleted: string;
  transfers: Array<{ model: string; field: string; records_transferred: number }>;
  total_records_transferred: number;
}

// Date Adjustment (Feature 6)
export interface DateAdjustmentResponse {
  message: string;
  direction: 'plus' | 'minus';
  total_rows_affected: number;
  details: Array<{ table: string; column: string; rows_affected: number }>;
}

export interface DateAdjustmentLog {
  id: number;
  direction: string;
  direction_display: string;
  affected_tables: Array<{ table: string; column: string; rows_affected: number }>;
  total_rows: number;
  performed_by: string;
  performed_at: string;
  notes: string;
}

// Compliance Certification (Feature 4)
export interface ComplianceCertification {
  id: number;
  company: number;
  company_name: string;
  certification_type: string;
  certification_type_display: string;
  name: string;
  document: string | null;
  expiry_date: string;
  issued_by: string;
  notes: string;
  status: 'valid' | 'expiring_soon' | 'expired';
  days_until_expiry: number;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

// Public branding response type
export interface PublicBranding {
  company_name: string;
  login_title?: string;
  login_color: string;
  button_color: string;
  background_color: string;
  font_color: string;
  company_logo?: string;
  company_dark_logo?: string;
  // CR-6243: absolute URLs resolved by the branding endpoints; absent until
  // an admin uploads one.
  header_logo?: string;
  login_image?: string;
}
